// Floating action buttons at the bottom right of every page: a "back to top" button that appears once you have scrolled, and any buttons you add
// (a link, a page, a phone number, e-mail...). Admins edit them in edit mode (FabEditor.js). Content: GET /api/fabs (apps/portal/site-footer.js).
// They sit above the floating footer card on desktop and above the bottom navigation bar on phones. Motion: M3 emphasized-decelerate.
(function () {
  const { Box } = MaterialUI;
  const { useState, useEffect } = React;

  const DEFAULT = { top: true, items: [], canEdit: false };
  const CACHE = 'wb_fabs';
  const cached = () => { try { return { ...DEFAULT, ...JSON.parse(localStorage.getItem(CACHE) || 'null'), canEdit: false }; } catch (e) { return DEFAULT; } };
  const pageHref = (page, sec, slug) => '#/' + [...(page === 'about' ? ['portfolio'] : ['p', page]), sec, slug].filter(Boolean).join('/');
  const hrefOf = t => window.SiteLinks.href(t);
  const ease = 'var(--m3-duration-long) var(--m3-easing-emphasized-decelerate)';
  const reduced = () => (window.M3Core ? window.M3Core.reducedMotion() : window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  function SiteFabs({ user }) {
    const { Fab, IconButton, Tooltip, GoogleIcon } = window.Components;
    const editOn = window.EditMode.use();
    const [data, setData] = useState(cached);
    const [scrolled, setScrolled] = useState(false);
    const [dialog, setDialog] = useState(false);
    const load = () => {
      let token = ''; try { token = localStorage.getItem('wb_token') || ''; } catch (e) { /* no storage */ }
      fetch('/api/fabs', token ? { headers: { Authorization: `Bearer ${token}` } } : undefined)
        .then(r => (r.ok ? r.json() : Promise.reject(new Error('fabs'))))
        .then(d => { setData(d); try { localStorage.setItem(CACHE, JSON.stringify({ top: d.top, items: d.items })); } catch (e) { /* no storage */ } })
        .catch(() => {});
    };
    useEffect(load, [user && user.id]);
    useEffect(() => {
      const on = () => setScrolled(window.scrollY > 480);
      on();
      window.addEventListener('scroll', on, { passive: true });
      return () => window.removeEventListener('scroll', on);
    }, []);

    const canEditHere = editOn && data.canEdit;
    if (!data.top && !data.items.length && !canEditHere) return null;
    const toTop = () => window.scrollTo({ top: 0, behavior: reduced() ? 'auto' : 'smooth' });

    return (
      <>
        <Box component="aside" aria-label="Quick actions" sx={{
          position: 'fixed', right: { xs: 'calc(max(0px, 50% - var(--wb-column, 100vw) / 2) + 16px)', md: 'calc(max(0px, 50% - var(--wb-column, 100vw) / 2) + 24px)' }, zIndex: 1150, display: 'flex', flexDirection: 'column-reverse', alignItems: 'flex-end', gap: 1.5,
          bottom: { xs: 'calc(80px + env(safe-area-inset-bottom, 0px) + 16px)', md: 88 }, pointerEvents: 'none', '& > *': { pointerEvents: 'auto' }
        }}>
          {/* Back to top: the row collapses (grid 0fr -> 1fr) so the buttons above it slide down when it is hidden */}
          {data.top && (
            <Box sx={{ display: 'grid', gridTemplateRows: scrolled ? '1fr' : '0fr', transition: `grid-template-rows ${ease}`, '@media (prefers-reduced-motion: reduce)': { transition: 'none' } }}>
              {/* No overflow clipping here: the button's shadow reaches past its box. While collapsed it is invisible (opacity 0, hidden), so nothing shows */}
              <Box sx={{ minHeight: 0 }}>
                <Box sx={{ opacity: scrolled ? 1 : 0, transform: scrolled ? 'scale(1)' : 'scale(0.6)', transition: `opacity ${ease}, transform ${ease}`, visibility: scrolled ? 'visible' : 'hidden', '@media (prefers-reduced-motion: reduce)': { transition: 'none' } }}>
                  <Tooltip title="Back to top" placement="left">
                    <Fab icon={<GoogleIcon name="arrow_upward" size={24} />} aria-label="Back to top" tone="primary" onClick={toTop} tabIndex={scrolled ? 0 : -1} />
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          )}
          {data.items.map((it, i) => {
            const href = hrefOf(it);
            const ext = /^https?:/.test(href);
            return (
              <Tooltip key={i} title={it.label} placement="left">
                <Fab size="small" tone="secondary" icon={<GoogleIcon name={it.icon || 'link'} size={20} />} aria-label={it.label} component="a" href={href}
                  {...(ext ? { target: '_blank', rel: 'noopener noreferrer' } : {})} />
              </Tooltip>
            );
          })}
          {canEditHere && (
            <Tooltip title="Edit buttons" placement="left">
              <IconButton variant="tonal" size="small" aria-label="Edit floating buttons" onClick={() => setDialog(true)}><GoogleIcon name="edit" size={20} /></IconButton>
            </Tooltip>
          )}
        </Box>
        {dialog && <window.Components.FabEditor data={data} onClose={() => setDialog(false)} onSaved={d => { setData(d); setDialog(false); load(); window.notify('Buttons saved'); }} />}
      </>
    );
  }

  window.Components = window.Components || {};
  window.Components.SiteFabs = SiteFabs;
})();
